'use client';
import React, { useEffect, useState } from 'react';
import FlameIcon from '@/assets/icons/Flame.svg';
import { getMemberDayOffer } from '@/helpers/api/member-day-offer';
import LinkWrapper from '@/components/link-wrapper/link-wrapper';
import { ITextAndImageCardBlock } from '@/lib/fragments/textAndImageCardBlock';
import {
  useGetLibraryConstantsDictionaryData,
  useTenantConfig,
} from '@/providers/ConfigProvider';

export interface MemberDayTileProps {
  businessUnitCode: string;
  nodeLocale: string;
  timezone?: string;
  cardList?: ITextAndImageCardBlock[];
  memberDaySlug?: string | null;
  memberDayCardText?: string | null;
}

function buildCountdownText(
  days: number,
  dictionary?: Record<string, string>,
): string {
  if (days <= 0) return dictionary?.happeningToday ?? 'Happening today';
  return `${dictionary?.happeningIn ?? 'Happening in'} ${days} ${dictionary?.day ?? 'day'}`;
}

const MemberDayTile = ({
  businessUnitCode,
  nodeLocale,
  timezone,
  cardList = [],
  memberDaySlug,
  memberDayCardText,
}: MemberDayTileProps) => {
  const dictionary = useGetLibraryConstantsDictionaryData();
  const config = useTenantConfig();
  const isLivliFamily = config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true';
  const [days, setDays] = useState<number | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!businessUnitCode || !nodeLocale) return;

    getMemberDayOffer({
      mpId: businessUnitCode,
      locale: nodeLocale,
      ...(timezone && { timezone }),
    })
      .then((data) => {
        if (data?.show) {
          setDays(data.days ?? 0);
          setVisible(true);
        }
      })
      .catch(() => {
        // Silently hide tile on error
      });
  }, [businessUnitCode, nodeLocale]);

  // Only show member day tile for Livli Family meeting places with a slug
  if (!isLivliFamily || !memberDaySlug) return null;

  if (!visible) return null;

  const cardText = memberDayCardText;

  if (!cardText) return null;

  const memberDayUrl = memberDaySlug ? `/${nodeLocale}/${memberDaySlug}` : null;

  if (!memberDayUrl) return null;

  return (
    <LinkWrapper data={memberDayUrl}>
      <div className="bg-brand-yellow-1 bg-dynamicStrokeYellow relative mb-5 flex min-h-[200px] animate-fadeIn flex-col justify-end overflow-hidden rounded-xl bg-cover p-6 text-neutral-grey-700 transition-all duration-200 z-10 lg:hover:brightness-90 animation-delay break-inside-avoid">
        {/* Countdown badge */}
        <div className="flex items-center gap-1 text-bodyl font-bold leading-[18px] mb-1">
          <FlameIcon className="h-8 w-8 shrink-0" aria-hidden="true" />
          <span>{buildCountdownText(days ?? 0, dictionary)}</span>
        </div>

        {/* Heading */}
        <h2 className="font-title text-headingm leading-tight">
          {cardText.split('\n').map((line: string, index: number) => (
            <React.Fragment key={index}>
              {line}
              {index < cardText.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </h2>
      </div>
    </LinkWrapper>
  );
};

export default MemberDayTile;
